import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Continue() {
  return (
    <div className="tutorial-container read-only">
      <h1 className="tutorial-title">⏭ JavaScript continue Statement</h1>

      {/* Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 What is continue?</h2>
        <p>
          The <code className="highlight_code ">continue</code> statement skips
          the current iteration of a loop and moves to the next iteration. It
          does not exit the loop entirely.
        </p>
        <p>
          At <b>Edu-Tech Master Academy</b>,{" "}
          <code className="highlight_code ">continue</code> is useful for
          skipping certain values or conditions in loops while continuing the
          rest.
        </p>
      </div>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{ background: "linear-gradient(135deg, #e0f2fe, #bae6fd)" }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code ">continue</code> skips the rest of
            the current loop iteration.
          </li>
          <li>
            Used inside <code className="highlight_code ">for</code>,{" "}
            <code className="highlight_code ">while</code>, or{" "}
            <code className="highlight_code ">do...while</code> loops.
          </li>
          <li>Loop execution continues with the next iteration.</li>
          <li>Useful for skipping unwanted or invalid data in loops.</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Skip a Number</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`for (let i = 1; i <= 5; i++) {
  if (i === 3) {
    continue; // Skip 3
  }
  console.log("Count at Edu-Tech Master Academy:", i);
}`}
          />
          <TryItButton
            code={`for (let i = 1; i <= 5; i++) {
  if (i === 3) {
    continue;
  }
  console.log("Count at Edu-Tech Master Academy:", i);
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Count at Edu-Tech Master Academy: 1{"\n"}
            Count at Edu-Tech Master Academy: 2{"\n"}
            Count at Edu-Tech Master Academy: 4{"\n"}
            Count at Edu-Tech Master Academy: 5
          </pre>
        </OutputBox>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Continue in while Loop</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let i = 0;
while (i < 5) {
  i++;
  if (i === 2) continue;
  console.log("Number:", i);
}`}
          />
          <TryItButton
            code={`let i = 0;
while (i < 5) {
  i++;
  if (i === 2) continue;
  console.log("Number:", i);
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Number: 1{"\n"}
            Number: 3{"\n"}
            Number: 4{"\n"}
            Number: 5
          </pre>
        </OutputBox>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Skip Even Numbers</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`for (let i = 1; i <= 10; i++) {
  if (i % 2 === 0) continue;
  console.log("Odd Number at Edu-Tech Master Academy:", i);
}`}
          />
          <TryItButton
            code={`for (let i = 1; i <= 10; i++) {
  if (i % 2 === 0) continue;
  console.log("Odd Number at Edu-Tech Master Academy:", i);
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Odd Number at Edu-Tech Master Academy: 1{"\n"}
            Odd Number at Edu-Tech Master Academy: 3{"\n"}
            Odd Number at Edu-Tech Master Academy: 5{"\n"}
            Odd Number at Edu-Tech Master Academy: 7{"\n"}
            Odd Number at Edu-Tech Master Academy: 9
          </pre>
        </OutputBox>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Skip Invalid Values in Array</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let data = [1, null, 2, undefined, 3];
for (let value of data) {
  if (value == null) continue;
  console.log("Valid Value at Edu-Tech Master Academy:", value);
}`}
          />
          <TryItButton
            code={`let data = [1, null, 2, undefined, 3];
for (let value of data) {
  if (value == null) continue;
  console.log("Valid Value at Edu-Tech Master Academy:", value);
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Valid Value at Edu-Tech Master Academy: 1{"\n"}
            Valid Value at Edu-Tech Master Academy: 2{"\n"}
            Valid Value at Edu-Tech Master Academy: 3
          </pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#1e40af" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice using <code className="highlight_code ">continue</code> in
            nested loops.
          </li>
          <li>
            Compare <code className="highlight_code ">continue</code> vs{" "}
            <code className="highlight_code ">break</code> to control loop
            behavior.
          </li>
          <li>
            Combine <code className="highlight_code ">continue</code> with
            arrays and conditional checks for data filtering.
          </li>
          <li>
            Experiment with loops over objects, sets, and maps using{" "}
            <code className="highlight_code ">continue</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
